import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { Info, X } from "lucide-react";

import {
  SYSTEM_CONFIG_ACCESS_HELP_DIALOG_DESCRIPTION,
  SYSTEM_CONFIG_ACCESS_HELP_DIALOG_TITLE,
  SYSTEM_CONFIG_ACCESS_HELP_HIGHLIGHTS,
  SYSTEM_CONFIG_ACCESS_HELP_SECTIONS,
} from "../content/systemConfigAccessHelpContent.js";

function HelpHighlightCard({ item }) {
  return (
    <article className="grid gap-1.5 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-3">
      <p className="m-0 text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-slate-500">{item.eyebrow}</p>
      <p className="m-0 text-[0.9rem] font-semibold tracking-[-0.02em] text-slate-950">{item.title}</p>
      <p className="m-0 text-[0.8rem] leading-5 text-slate-600">{item.description}</p>
    </article>
  );
}

function HelpSection({ section }) {
  return (
    <article className="grid gap-2 rounded-[4px] border border-black/6 bg-white px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="grid gap-1">
        <p className="m-0 text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-slate-500">{section.title}</p>
      </div>

      {Array.isArray(section.paragraphs)
        ? section.paragraphs.map((paragraph) => (
          <p key={paragraph} className="m-0 text-[0.84rem] leading-6 text-slate-600">
            {paragraph}
          </p>
        ))
        : null}

      {Array.isArray(section.items) && section.items.length > 0 ? (
        <ul className="m-0 grid gap-1.5 pl-4 text-[0.8rem] leading-5 text-slate-700">
          {section.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

export default function SystemConfigAccessHelpDialog({ isOpen = false, onClose }) {
  const closeButtonRef = React.useRef(null);
  const previouslyFocusedElementRef = React.useRef(null);
  const titleId = React.useId();
  const descriptionId = React.useId();

  const handleClose = React.useCallback(() => {
    if (typeof onClose === "function") {
      onClose();
    }
  }, [onClose]);

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    previouslyFocusedElementRef.current = document.activeElement;
    const frameId = typeof window !== "undefined"
      ? window.requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      })
      : null;

    return () => {
      if (typeof window !== "undefined" && frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      const previousElement = previouslyFocusedElementRef.current;
      if (previousElement && typeof previousElement.focus === "function") {
        previousElement.focus();
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [handleClose, isOpen]);

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const dialogContent = (
    <div className="fixed inset-0 z-modal isolate" data-system-config-access-help-dialog="true">
      <div
        className="absolute inset-0 bg-slate-950/45 px-4 py-4"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            handleClose();
          }
        }}
      >
        <div className="flex min-h-full items-center justify-center">
          <section
            id="system-config-access-help-dialog"
            aria-describedby={descriptionId}
            aria-labelledby={titleId}
            aria-modal="true"
            className="grid max-h-[calc(100dvh-2rem)] w-full max-w-[min(70rem,calc(100vw-1.5rem))] overflow-hidden rounded-[4px] border border-black/6 bg-white shadow-[0_28px_64px_rgba(15,23,42,0.18)]"
            role="dialog"
          >
            <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(255,255,255,0.98))] px-4 py-3.5 max-[720px]:px-3.5">
              <div className="flex min-w-0 items-start gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[4px] border border-slate-200 bg-slate-100 text-slate-700">
                  <Info aria-hidden="true" size={18} />
                </span>
                <div className="grid gap-1">
                  <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-slate-500">Aide contextuelle</p>
                  <h2 id={titleId} className="m-0 text-[1.05rem] font-semibold tracking-[-0.025em] text-slate-950">
                    {SYSTEM_CONFIG_ACCESS_HELP_DIALOG_TITLE}
                  </h2>
                  <p id={descriptionId} className="m-0 max-w-[58rem] text-[0.82rem] leading-5 text-slate-600">
                    {SYSTEM_CONFIG_ACCESS_HELP_DIALOG_DESCRIPTION}
                  </p>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                aria-label="Fermer l’aide RBAC"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-950 focus:outline-hidden focus:ring-2 focus:ring-border-accent-subtle"
                onClick={handleClose}
                type="button"
              >
                <X aria-hidden="true" size={16} />
              </button>
            </header>

            <div className="overflow-y-auto px-4 py-4 max-[720px]:px-3.5">
              <div className="grid gap-3">
                <div className="grid gap-3 min-[1040px]:grid-cols-3">
                  {SYSTEM_CONFIG_ACCESS_HELP_HIGHLIGHTS.map((item) => (
                    <HelpHighlightCard item={item} key={item.title} />
                  ))}
                </div>

                <div className="grid gap-3">
                  {SYSTEM_CONFIG_ACCESS_HELP_SECTIONS.map((section) => (
                    <HelpSection key={section.id} section={section} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );

  const portalRoot = getReactPortalRoot();
  if (portalRoot instanceof HTMLElement) {
    return createPortal(dialogContent, portalRoot);
  }

  return dialogContent;
}